import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValueEvent, useScroll, useSpring, useTransform } from "framer-motion";
import Icon from "../../common/Icon";
import { storyScenes, integrationAreas, corePromise } from "../../../Data/siteContent";
import {
  VIEW,
  WorldDefs,
  Backdrop,
  StageBrief,
  StageDiscovery,
  StageEngineering,
  StageSolution,
  StageIntegration,
  StageVista,
  useWindow,
} from "./world";
import "./story.css";

const N = storyScenes.length; // 6
const SPAN = 1 / N;

/* Cross-fade window for scene i: held at full opacity across its own slice,
   dissolving into its neighbour only at the seam so the world never cuts.
   The first and last scenes are pinned on, or the story would open and close
   on a half-faded frame. */
function useStageOpacity(p, i) {
  const fade = SPAN * 0.12;
  const s = i * SPAN;
  const e = (i + 1) * SPAN;
  const first = i === 0;
  const last = i === N - 1;
  return useTransform(
    p,
    [first ? -0.001 : s - fade, first ? 0 : s, last ? 1 : e - fade, last ? 1.001 : e],
    [first ? 1 : 0, 1, 1, last ? 1 : 0]
  );
}

/* Framing the world.

   The scenes live inside a fixed band of the 1200x760 coordinate space. Rather
   than letting preserveAspectRatio crop it (which cut the header off scene 4's
   panel on laptop-shaped screens), we size the viewBox to the stage aspect so
   the whole band is always visible.

   Landscape: fit the band's HEIGHT, let extra width show either side. Content
   sits right of centre so the narration column on the left stays clear.
   Portrait: fit the band's WIDTH and bias the content upward, because the
   narration sits underneath on a phone rather than beside. Scenes 5 and 6 also
   switch to a 2 x 3 grid there - see grid() in world.js. */
const BAND = {
  landscape: { cx: 600, cy: 300, w: 1200, h: 470 },
  portrait: { cx: 600, cy: 300, w: 470, h: 500, bias: 0.42 },
};

function useFraming(stageRef, worldRef) {
  const [frame, setFrame] = useState({
    viewBox: `0 0 ${VIEW.w} ${VIEW.h}`,
    portrait: false,
  });
  useEffect(() => {
    const stage = stageRef.current;
    const world = worldRef.current;
    if (!stage || !world || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const stageBox = stage.getBoundingClientRect();
      // Orientation comes from the STAGE (it decides the page layout), but the
      // viewBox must be sized from the SVG's own box - in portrait the SVG only
      // fills the top half, so using the stage aspect framed the world wrong.
      const { width, height } = world.getBoundingClientRect();
      if (!width || !height || !stageBox.height) return;
      const portrait = stageBox.width / stageBox.height < 1.15;
      const aspect = width / height;
      const b = portrait ? BAND.portrait : BAND.landscape;
      let w;
      let h;
      if (aspect > b.w / b.h) {
        h = b.h;
        w = h * aspect;
      } else {
        w = b.w;
        h = w / aspect;
      }
      const y = portrait ? b.cy - h * b.bias : b.cy - h / 2;
      setFrame((f) => {
        const viewBox = `${Math.round(b.cx - w / 2)} ${Math.round(y)} ${Math.round(w)} ${Math.round(h)}`;
        return f.viewBox === viewBox && f.portrait === portrait ? f : { viewBox, portrait };
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(stage);
    ro.observe(world);
    return () => ro.disconnect();
  }, [stageRef, worldRef]);
  return frame;
}

export default function BusinessStory() {
  const ref = useRef(null);
  const stageRef = useRef(null);
  const worldRef = useRef(null);
  const [active, setActive] = useState(0);
  const [simple, setSimple] = useState(false);

  // Only prefers-reduced-motion falls back to stacked scenes now. Phones get
  // the scroll story too, reframed for portrait (see useViewBox / grid()).
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setSimple(reduce.matches);
    update();
    reduce.addEventListener("change", update);
    return () => reduce.removeEventListener("change", update);
  }, []);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  // Smoothing keeps the camera from jittering on trackpad/inertial scroll.
  const p = useSpring(scrollYProgress, { stiffness: 90, damping: 26, mass: 0.4, restDelta: 0.0005 });

  useMotionValueEvent(p, "change", (v) => {
    // +0.06 == half the 0.12 crossfade band, so the copy swaps mid-dissolve
    const i = Math.min(N - 1, Math.max(0, Math.floor(v / SPAN + 0.06)));
    setActive((cur) => (cur === i ? cur : i));
  });

  /* ---- the camera: one continuous dolly through the world ---- */
  const { viewBox, portrait } = useFraming(stageRef, worldRef);
  const camScale = useTransform(p, [0, 0.17, 0.34, 0.5, 0.68, 0.84, 1], [1.06, 1.0, 1.05, 1.0, 0.95, 0.88, 0.84]);
  const camY = useTransform(p, [0, 0.34, 0.68, 1], [18, 0, -26, -46]);
  const camXDesktop = useTransform(p, [0, 0.08, 0.6, 0.72, 1], [134, 122, 122, 46, 38]);
  const camX = portrait ? 0 : camXDesktop;

  /* ---- persistent backdrop responds to the whole journey ---- */
  const gridOpacity = useTransform(p, [0, 0.3, 0.62, 1], [0.16, 0.3, 0.5, 0.72]);
  const auraOpacity = useTransform(p, [0, 0.28, 0.62, 1], [0.35, 0.9, 1, 0.55]);
  const warmOpacity = useTransform(p, [0.6, 0.82, 1], [0, 0.55, 1]);
  const horizonGlow = useTransform(p, [0.45, 0.7, 1], [0.1, 0.5, 0.95]);

  /* ---- per-stage visibility ---- */
  const o0 = useStageOpacity(p, 0);
  const o1 = useStageOpacity(p, 1);
  const o2 = useStageOpacity(p, 2);
  const o3 = useStageOpacity(p, 3);
  const o4 = useStageOpacity(p, 4);
  const o5 = useStageOpacity(p, 5);

  /* ---- per-stage internal drivers ---- */
  // 1. brief flies in
  const briefEnter = useTransform(p, [0, 0.11], [-320, 0]);
  // 2. scattered -> sorted, scan sweep, links drawn
  const sortT = useWindow(p, SPAN * 1.05, SPAN * 1.85);
  const scanX = useTransform(p, [SPAN, SPAN * 2], [280, 830]);
  const linkLength = useWindow(p, SPAN * 1.45, SPAN * 1.95);
  // 3. frame drawn, modules snap in
  const frameLength = useWindow(p, SPAN * 2.02, SPAN * 2.5);
  const assembleT = useWindow(p, SPAN * 2.2, SPAN * 2.98);
  // 4. system goes live
  const liveT = useWindow(p, SPAN * 2.9, SPAN * 3.45);
  const pulse = useTransform(p, [SPAN * 3.2, SPAN * 3.5], [0, 1]);
  // 5. wires drop into business functions
  const wireLength = useWindow(p, SPAN * 4.05, SPAN * 4.55);
  const dropT = useWindow(p, SPAN * 4.15, SPAN * 4.95);
  // 6. the vista
  const flowT = useWindow(p, SPAN * 5.05, SPAN * 5.6);
  const growthLength = useWindow(p, SPAN * 5.25, SPAN * 5.9);
  const skylineT = useWindow(p, SPAN * 4.85, SPAN * 5.45);

  const scene = storyScenes[active];

  if (simple) return <StorySimple />;

  return (
    <section className={"cv-bstory" + (portrait ? " is-portrait" : "")} ref={ref} id="story" aria-label="How CafeVista turns a business challenge into a business solution">
      <div className="cv-bstory__stage" ref={stageRef}>
        <motion.svg
          ref={worldRef}
          className="cv-bstory__world"
          viewBox={viewBox}
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true"
        >
          <WorldDefs />
          <Backdrop
            gridOpacity={gridOpacity}
            auraOpacity={auraOpacity}
            warmOpacity={warmOpacity}
            horizonGlow={horizonGlow}
            skylineT={skylineT}
          />
          <motion.g style={{ scale: camScale, x: camX, y: camY, transformBox: "view-box", transformOrigin: "600px 380px" }}>
            <motion.g style={{ opacity: o0 }}><StageBrief enter={briefEnter} /></motion.g>
            <motion.g style={{ opacity: o1 }}><StageDiscovery sortT={sortT} scanX={scanX} linkLength={linkLength} /></motion.g>
            <motion.g style={{ opacity: o2 }}><StageEngineering assembleT={assembleT} frameLength={frameLength} /></motion.g>
            <motion.g style={{ opacity: o3 }}><StageSolution liveT={liveT} pulse={pulse} /></motion.g>
            <motion.g style={{ opacity: o4 }}><StageIntegration areas={integrationAreas} dropT={dropT} wireLength={wireLength} portrait={portrait} /></motion.g>
            <motion.g style={{ opacity: o5 }}><StageVista areas={integrationAreas} flowT={flowT} growthLength={growthLength} portrait={portrait} /></motion.g>
          </motion.g>
        </motion.svg>

        <div className="cv-bstory__vignette" aria-hidden="true" />

        {/* narration */}
        <div className="cv-bstory__copy">
          {/* Keyed remount, deliberately NOT wrapped in AnimatePresence: a fast
              scroll or an anchor jump steps through several scenes at once, and
              mode="wait" queues exit-then-enter until the copy is blank on
              screen. Remounting always paints the current scene immediately. */}
          <motion.div
            key={scene.id}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.42, ease: [0.21, 0.8, 0.25, 1] }}
          >
            <span className="cv-bstory__label">
              <span className="cv-bstory__step">{scene.step}</span>
              {scene.label}
            </span>
            <h2 className="cv-bstory__title">{scene.title}</h2>
            <p className="cv-bstory__body">{scene.body}</p>
          </motion.div>

          {active === N - 1 && (
            <motion.div
              className="cv-bstory__outro"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <Link to="/contactus" className="cv-btn">
                Bring us your challenge <Icon name="arrow" size={18} className="arrow" />
              </Link>
            </motion.div>
          )}
        </div>

        {/* progress rail */}
        <div className="cv-bstory__rail" aria-hidden="true">
          <motion.span className="cv-bstory__rail-fill" style={{ scaleY: p }} />
          {storyScenes.map((s, i) => (
            <span key={s.id} className={"cv-bstory__rail-node" + (i <= active ? " is-on" : "")}>
              <i />
              <em>{s.step}</em>
            </span>
          ))}
        </div>

        <div className="cv-bstory__hint" aria-hidden="true">
          <span className="cv-bstory__hint-mouse"><span /></span>
          Scroll through the process
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------
   Stacked fallback: same six beats, no sticky stage.
   Mobile and prefers-reduced-motion land here.
   ------------------------------------------------------------------ */
function StorySimple() {
  return (
    <section className="cv-bstory cv-bstory--simple" id="story">
      <div className="cv-bstory__simple-head">
        <span className="cv-eyebrow"><span className="dot" />{corePromise.eyebrow}</span>
        <h2 className="cv-h2">{corePromise.title}</h2>
        <p className="cv-lead">{corePromise.lead}</p>
      </div>

      <ol className="cv-bstory__simple-list">
        {storyScenes.map((s) => (
          <motion.li
            key={s.id}
            className="cv-bstory__simple-item cv-glass"
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
          >
            <span className="cv-bstory__simple-step">{s.step}</span>
            <div>
              <span className="cv-bstory__simple-label">{s.label}</span>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </div>
          </motion.li>
        ))}
      </ol>

      <div className="cv-bstory__simple-cta">
        <Link to="/contactus" className="cv-btn">
          Bring us your challenge <Icon name="arrow" size={18} className="arrow" />
        </Link>
      </div>
    </section>
  );
}
